import { useCallback, useEffect, useMemo, useState } from "react";
import {
    AlertTriangle,
    CalendarCheck2,
    CalendarDays,
    Download,
    FileText,
    RefreshCw,
    ShieldAlert,
    Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import api from "../../api/axios";
import AdminNavbar from "../../components/layout/AdminNavbar";

const number = (value) => Number(value || 0).toLocaleString("en-IN");
const pct = (value) => `${Number(value || 0)}%`;
const asObject = (value) => (value && typeof value === "object" && !Array.isArray(value) ? value : {});
const asArray = (value) => (Array.isArray(value) ? value : []);
const getCurrentPeriodKey = () => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};
const dateText = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-IN");
};

const Panel = ({ title, subtitle, icon: Icon, children, className = "" }) => (
    <section className={`rounded-2xl border border-slate-200 bg-[var(--card-bg)] p-4 shadow-sm ${className}`}>
        <div className="mb-3 flex items-start justify-between gap-3">
            <div>
                <h2 className="text-lg font-extrabold text-slate-950">{title}</h2>
                {subtitle && <p className="mt-1 text-sm font-semibold text-slate-500">{subtitle}</p>}
            </div>
            {Icon && <Icon size={18} className="text-slate-700" />}
        </div>
        {children}
    </section>
);

const MiniCard = ({ label, value, tone = "slate", onClick }) => {
    const tones = {
        slate: "border-slate-200 bg-[var(--bg-secondary)] text-slate-950",
        green: "border-emerald-200 bg-emerald-50 text-emerald-800",
        amber: "border-amber-200 bg-amber-50 text-amber-800",
        red: "border-red-200 bg-red-50 text-red-800",
        violet: "border-[var(--accent-soft)] bg-[var(--accent-light)] text-violet-800",
    };
    return (
        <button
            type="button"
            onClick={onClick}
            className={`rounded-xl border p-3 text-left transition hover:-translate-y-0.5 hover:shadow-md ${tones[tone] || tones.slate} ${onClick ? "cursor-pointer" : "cursor-default"}`}
        >
            <p className="text-xs font-extrabold uppercase tracking-wide text-slate-500">{label}</p>
            <p className="mt-2 text-2xl font-black">{value}</p>
        </button>
    );
};

const StatusRow = ({ label, value, tone = "slate", onClick }) => {
    const toneClass = {
        slate: "text-slate-900",
        green: "text-emerald-700",
        amber: "text-amber-700",
        red: "text-red-700",
    };
    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex w-full items-center justify-between gap-3 rounded-xl bg-[var(--bg-secondary)] px-3 py-2 text-left transition ${onClick ? "hover:bg-slate-100 hover:shadow-sm" : "cursor-default"}`}
        >
            <span className="text-sm font-bold text-slate-600">{label}</span>
            <span className={`text-sm font-black ${toneClass[tone] || toneClass.slate}`}>{value}</span>
        </button>
    );
};

const defaultData = {
    dashboard: {
        summary: {},
        attendanceHealth: {},
        lifecycle: { anniversaries: [] },
        approvals: {},
        payrollReadiness: {},
        riskAlerts: [],
        teams: [],
        hiring: {},
        onboarding: [],
        offboarding: [],
        documents: {},
        complaints: { teamWise: {} },
        smartAlerts: [],
    },
};
const CACHE_KEY_PREFIX = "aviatricks.hr.dashboard.cache";
const readCachedDashboard = (periodKey) => {
    try {
        const cached = JSON.parse(localStorage.getItem(`${CACHE_KEY_PREFIX}.${periodKey}`) || "null");
        return asObject(cached).dashboard ? cached : defaultData;
    } catch {
        return defaultData;
    }
};

const normalizeDashboardPayload = (payload) => {
    const safePayload = asObject(payload);
    const rawDashboard = asObject(safePayload.dashboard);
    const dashboard = {
        ...defaultData.dashboard,
        ...rawDashboard,
        summary: { ...defaultData.dashboard.summary, ...asObject(rawDashboard.summary) },
        attendanceHealth: { ...defaultData.dashboard.attendanceHealth, ...asObject(rawDashboard.attendanceHealth) },
        lifecycle: { ...defaultData.dashboard.lifecycle, ...asObject(rawDashboard.lifecycle) },
        approvals: { ...defaultData.dashboard.approvals, ...asObject(rawDashboard.approvals) },
        payrollReadiness: { ...defaultData.dashboard.payrollReadiness, ...asObject(rawDashboard.payrollReadiness) },
        hiring: { ...defaultData.dashboard.hiring, ...asObject(rawDashboard.hiring) },
        documents: { ...defaultData.dashboard.documents, ...asObject(rawDashboard.documents) },
        complaints: { ...defaultData.dashboard.complaints, ...asObject(rawDashboard.complaints) },
        riskAlerts: asArray(rawDashboard.riskAlerts),
        smartAlerts: asArray(rawDashboard.smartAlerts),
        teams: asArray(rawDashboard.teams),
        onboarding: asArray(rawDashboard.onboarding),
        offboarding: asArray(rawDashboard.offboarding),
    };

    dashboard.lifecycle.anniversaries = asArray(dashboard.lifecycle.anniversaries);
    return { ...safePayload, dashboard };
};

const HrCommand = () => {
    const navigate = useNavigate();
    const [periodKey, setPeriodKey] = useState(getCurrentPeriodKey);
    const [data, setData] = useState(() => normalizeDashboardPayload(readCachedDashboard(getCurrentPeriodKey())));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [updatedAt, setUpdatedAt] = useState("");

    const dashboard = normalizeDashboardPayload(data).dashboard;
    const summary = dashboard.summary || {};
    const attendance = dashboard.attendanceHealth || {};
    const lifecycle = dashboard.lifecycle || {};
    const approvals = dashboard.approvals || {};
    const payroll = dashboard.payrollReadiness || {};
    const hiring = dashboard.hiring || {};
    const documents = dashboard.documents || {};
    const complaints = dashboard.complaints || {};
    const teams = dashboard.teams;
    const riskAlerts = dashboard.riskAlerts;
    const smartAlerts = dashboard.smartAlerts;
    const onboarding = dashboard.onboarding;
    const offboarding = dashboard.offboarding;

    const loadDashboard = useCallback(async ({ signal, force = false } = {}) => {
        try {
            setLoading(true);
            const res = await api.get("/api/admin/hr/dashboard", {
                cache: !force,
                params: { view: "dashboard", limit: 8, periodKey, ...(force ? { _t: Date.now() } : {}) },
                signal,
                timeout: 10000,
            });
            const nextData = normalizeDashboardPayload(res.data || defaultData);
            setData(nextData);
            localStorage.setItem(`${CACHE_KEY_PREFIX}.${periodKey}`, JSON.stringify(nextData));
            setUpdatedAt(new Date().toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" }));
            setError("");
        } catch (err) {
            if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
            const timedOut = err.code === "ECONNABORTED" || String(err.message || "").toLowerCase().includes("timeout");
            setError(timedOut ? "Showing saved HR data. Latest update crossed 10 seconds." : err.response?.data?.message || err.message || "Unable to load HR dashboard.");
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    }, [periodKey]);

    useEffect(() => {
        document.title = "HR Command | Savrion Technologies";
        setData(normalizeDashboardPayload(readCachedDashboard(periodKey)));
        const controller = new AbortController();
        const id = window.setTimeout(() => loadDashboard({ signal: controller.signal, force: true }), 0);
        return () => {
            window.clearTimeout(id);
            controller.abort();
        };
    }, [loadDashboard, periodKey]);

    const reportRows = useMemo(() => {
        const summaryRows = dashboard.summary || {};
        const attendanceRows = dashboard.attendanceHealth || {};
        const payrollRows = dashboard.payrollReadiness || {};
        const rows = [
            ["HR Monthly Report"],
            ["Total employees", summaryRows.totalEmployees || 0],
            ["Present today", summaryRows.presentToday || 0],
            ["Absent today", summaryRows.absentToday || 0],
            ["Late check-ins", summaryRows.lateCheckIns || 0],
            ["Pending approvals", summaryRows.pendingApprovals || 0],
            ["Open complaints", summaryRows.openComplaints || 0],
            [],
            ["Attendance"],
            ["Attendance percent", attendanceRows.attendancePercentage || 0],
            ["Missing checkout", attendanceRows.missingCheckout || 0],
            ["Work from home", attendanceRows.workFromHome || 0],
            ["Leave today", attendanceRows.leaveCount || 0],
            [],
            ["Payroll"],
            ["Missing bank details", payrollRows.missingBankDetails || 0],
            ["Missing PAN/UAN", payrollRows.missingPanUan || 0],
            ["Salary slip pending", payrollRows.salarySlipPending || 0],
            ["Payroll status", payrollRows.payrollLockStatus || "Open"],
        ];
        rows.push([], ["Team", "Headcount", "Present", "Leave", "Late", "Pending", "Payroll ready"]);
        teams.forEach((team) => {
            rows.push([
                team.team,
                team.headcount,
                team.presentToday,
                team.leaveCount,
                team.lateCount,
                team.pendingRequests,
                team.payrollReady,
            ]);
        });
        return rows;
    }, [dashboard, teams]);

    const exportExcel = async () => {
        const XLSX = await import("xlsx");
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(reportRows), "HR Report");
        XLSX.utils.book_append_sheet(
            workbook,
            XLSX.utils.json_to_sheet(teams),
            "Team Status",
        );
        XLSX.writeFile(workbook, "hr-monthly-report.xlsx");
    };

    const exportPdf = async () => {
        const { default: jsPDF } = await import("jspdf");
        const doc = new jsPDF({ unit: "pt", format: "a4" });
        let y = 42;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.text("HR Monthly Report", 40, y);
        y += 24;
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        reportRows.flatMap((row) => (row.length ? [row.join("  :  ")] : [""])).forEach((line) => {
            if (y > 780) {
                doc.addPage();
                y = 40;
            }
            doc.text(String(line).slice(0, 105), 40, y);
            y += 16;
        });
        doc.save("hr-monthly-report.pdf");
    };

    const go = (path, state) => () => navigate(path, state ? { state } : undefined);

    return (
        <div className="flex flex-col xl:flex-row min-h-screen bg-[var(--bg-secondary)] text-slate-950 dark:bg-[#0d0d0f] dark:text-white">
            <AdminNavbar />
            <main className="min-w-0 flex-1 px-4 py-5 lg:px-6">
                <section className="rounded-2xl border border-slate-200 bg-[var(--card-bg)] p-5 shadow-sm">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <p className="text-xs font-black uppercase tracking-[0.28em] text-[var(--heading-secondary)]">HR Department</p>
                            <h1 className="mt-2 text-3xl font-black text-slate-950">HR command dashboard</h1>
                            <p className="mt-1 text-sm font-semibold text-slate-600">
                                Attendance, approvals, payroll readiness, employee movement, documents, and complaints.
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <label className="relative">
                                <input type="month" value={periodKey} onChange={(event) => setPeriodKey(event.target.value)} className="h-10 rounded-xl border border-slate-200 bg-[var(--card-bg)] px-3 pr-9 text-sm font-black text-slate-900 shadow-sm outline-none" />
                                <CalendarDays size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
                            </label>
                            <button onClick={exportExcel} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-[var(--card-bg)] px-4 py-2 text-sm font-black text-slate-900 shadow-sm hover:bg-[var(--bg-secondary)]">
                                <Download size={16} /> Excel Report
                            </button>
                            <button onClick={exportPdf} className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-4 py-2 text-sm font-black text-white shadow-sm hover:bg-slate-800">
                                <FileText size={16} /> PDF Report
                            </button>
                        </div>
                    </div>
                </section>

                {(loading || updatedAt || error) && (
                    <div className={`mt-4 rounded-xl border px-4 py-2 text-sm font-bold ${
                        error
                            ? "border-red-200 bg-red-50 text-red-700"
                            : "border-slate-200 bg-[var(--card-bg)] text-slate-500"
                    }`}>
                        {error || (loading ? "Updating latest HR data..." : `Updated ${updatedAt}`)}
                    </div>
                )}

                <section className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
                    <MiniCard label="Employees" value={number(summary.totalEmployees)} tone="violet" onClick={go("/admin/employees")} />
                    <MiniCard label="Present" value={number(summary.presentToday)} tone="green" onClick={go("/admin/attendance")} />
                    <MiniCard label="Absent" value={number(summary.absentToday)} tone={summary.absentToday ? "red" : "slate"} onClick={go("/admin/attendance")} />
                    <MiniCard label="Late" value={number(summary.lateCheckIns)} tone={summary.lateCheckIns ? "amber" : "slate"} onClick={go("/admin/attendance")} />
                    <MiniCard label="Pending" value={number(summary.pendingApprovals)} tone={summary.pendingApprovals ? "amber" : "slate"} onClick={go("/admin/approval-inbox")} />
                    <MiniCard label="Complaints" value={number(summary.openComplaints)} tone={summary.openComplaints ? "red" : "slate"} onClick={go("/admin/complaints")} />
                </section>

                <section className="mt-4 grid gap-3 xl:grid-cols-4">
                    {smartAlerts.length ? smartAlerts.map((item) => (
                        <button
                            key={item.key || item.title}
                            type="button"
                            onClick={go(item.route || "/admin/approval-inbox")}
                            className={`rounded-2xl border p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
                                item.tone === "red"
                                    ? "border-red-200 bg-red-50 text-red-900"
                                    : item.tone === "green"
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                                    : "border-amber-200 bg-amber-50 text-amber-900"
                            }`}
                        >
                            <p className="text-xs font-black uppercase tracking-wide opacity-70">{item.label || "Smart alert"}</p>
                            <p className="mt-2 text-2xl font-black">{number(item.count)}</p>
                            <p className="mt-1 text-xs font-bold opacity-80">{item.title}</p>
                        </button>
                    )) : (
                        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-bold text-emerald-800 xl:col-span-4">
                            Smart daily alerts are clear right now.
                        </div>
                    )}
                </section>

                <section className="mt-4 grid gap-4 xl:grid-cols-3">
                    <Panel title="Attendance Health" subtitle="Today and this month" icon={CalendarCheck2}>
                        <div className="grid gap-2 sm:grid-cols-2">
                            <StatusRow label="Attendance" value={pct(attendance.attendancePercentage)} tone="green" />
                            <StatusRow label="Late trend" value={number(attendance.lateComingTrend)} tone={attendance.lateComingTrend ? "amber" : "green"} />
                            <StatusRow label="Missing checkout" value={number(attendance.missingCheckout)} tone={attendance.missingCheckout ? "red" : "green"} onClick={go("/admin/attendance")} />
                            <StatusRow label="WFH" value={number(attendance.workFromHome)} onClick={go("/admin/wfh")} />
                            <StatusRow label="Leave today" value={number(attendance.leaveCount)} />
                            <StatusRow label="Early checkout" value={number(attendance.earlyCheckoutCount)} />
                        </div>
                    </Panel>

                    <Panel title="Payroll Readiness" subtitle="Items HR must fix before salary" icon={ShieldAlert}>
                        <div className="grid gap-2">
                            <StatusRow label="Missing bank details" value={number(payroll.missingBankDetails)} tone={payroll.missingBankDetails ? "red" : "green"} onClick={go("/admin/employees")} />
                            <StatusRow label="Missing PAN/UAN" value={number(payroll.missingPanUan)} tone={payroll.missingPanUan ? "red" : "green"} onClick={go("/admin/employees")} />
                            <StatusRow label="Loss of pay" value={number(payroll.lossOfPayCount)} tone={payroll.lossOfPayCount ? "amber" : "green"} />
                            <StatusRow label="Unapproved leaves" value={number(payroll.unapprovedLeaves)} tone={payroll.unapprovedLeaves ? "amber" : "green"} />
                            <StatusRow label="Salary slip pending" value={number(payroll.salarySlipPending)} tone={payroll.salarySlipPending ? "amber" : "green"} onClick={go("/admin/employees")} />
                            <StatusRow label="Payroll status" value={payroll.payrollLockStatus || "Open"} tone={payroll.payrollLockStatus === "Locked" ? "green" : "amber"} />
                        </div>
                    </Panel>

                    <Panel title="Approval Center" subtitle="Pending work for HR/admin" icon={Users}>
                        <div className="grid gap-2">
                            <StatusRow label="Leave approvals" value={number(approvals.leave)} onClick={go("/admin/leave")} />
                            <StatusRow label="WFH requests" value={number(approvals.wfh)} tone={approvals.wfh ? "amber" : "green"} onClick={go("/admin/approval-inbox")} />
                            <StatusRow label="Attendance corrections" value={number(approvals.attendanceCorrections)} tone={approvals.attendanceCorrections ? "amber" : "green"} onClick={go("/admin/approval-inbox")} />
                            <StatusRow label="Expense approvals" value={number(approvals.expenses)} onClick={go("/admin/expenses")} />
                            <StatusRow label="Employee requests" value={number(approvals.requests)} onClick={go("/admin/complaints")} />
                            <StatusRow label="Profile changes" value={number(approvals.profileChanges)} onClick={go("/admin/approval-inbox")} />
                            <StatusRow label="Payslip issues" value={number(approvals.payslipIssues)} onClick={go("/admin/approval-inbox")} />
                            <StatusRow label="Offboarding approvals" value={number(approvals.offboarding)} onClick={go("/admin/offboarding")} />
                        </div>
                    </Panel>
                </section>

                <section className="mt-4 grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
                    <Panel title="Team-wise HR View" subtitle="Headcount, attendance, requests, and payroll status">
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[760px] text-left text-sm">
                                <thead className="text-xs uppercase text-slate-500">
                                    <tr className="border-b border-slate-200">
                                        <th className="py-2">Team</th>
                                        <th>Headcount</th>
                                        <th>Present</th>
                                        <th>Leave</th>
                                        <th>Late</th>
                                        <th>Pending</th>
                                        <th>Payroll ready</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {teams.map((team) => (
                                        <tr key={team.team} className="border-b border-slate-100 font-bold">
                                            <td className="py-3">{team.team}</td>
                                            <td>{number(team.headcount)}</td>
                                            <td className="text-emerald-700">{number(team.presentToday)}</td>
                                            <td>{number(team.leaveCount)}</td>
                                            <td className={team.lateCount ? "text-amber-700" : ""}>{number(team.lateCount)}</td>
                                            <td className={team.pendingRequests ? "text-amber-700" : ""}>{number(team.pendingRequests)}</td>
                                            <td>{number(team.payrollReady)} / {number(team.headcount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>

                    <Panel title="Risk Alerts" subtitle="Simple warnings HR should check" icon={AlertTriangle}>
                        <div className="grid max-h-[310px] gap-2 overflow-y-auto pr-1">
                            {riskAlerts.length ? riskAlerts.map((alert, index) => (
                                <div key={`${alert.title}-${index}`} className="rounded-xl border border-red-100 bg-red-50 px-3 py-2">
                                    <div className="flex items-center justify-between gap-2">
                                        <p className="text-sm font-black text-red-900">{alert.title}</p>
                                        <span className="rounded-full bg-[var(--card-bg)] px-2 py-1 text-xs font-black text-red-700">{alert.level}</span>
                                    </div>
                                    <p className="mt-1 text-xs font-bold text-red-700">{alert.detail}</p>
                                    {Array.isArray(alert.evidence) && alert.evidence.length > 0 && (
                                        <div className="mt-2 grid gap-1">
                                            {alert.evidence.slice(0, 3).map((item, evidenceIndex) => (
                                                <p key={`${item.date || evidenceIndex}-${item.timing || evidenceIndex}`} className="text-[11px] font-extrabold text-red-800">
                                                    {item.dateLabel || item.date || "Date not recorded"}
                                                    {item.timing ? ` - ${item.timing}` : ""}
                                                </p>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )) : <p className="rounded-xl bg-emerald-50 px-3 py-3 text-sm font-bold text-emerald-700">No major HR alerts right now.</p>}
                        </div>
                    </Panel>
                </section>

                <section className="mt-4 grid gap-4 xl:grid-cols-3">
                    <Panel title="Employee Lifecycle" subtitle="Joining, probation, notice, exits">
                        <div className="grid gap-2 sm:grid-cols-2">
                            <StatusRow label="New joiners" value={number(lifecycle.newJoiners)} />
                            <StatusRow label="Probation" value={number(lifecycle.probation)} />
                            <StatusRow label="Confirmed" value={number(lifecycle.confirmed)} />
                            <StatusRow label="Interns" value={number(lifecycle.interns)} />
                            <StatusRow label="Notice period" value={number(lifecycle.noticePeriod)} tone={lifecycle.noticePeriod ? "amber" : "green"} />
                            <StatusRow label="Ex-employees" value={number(lifecycle.exEmployees)} />
                        </div>
                    </Panel>

                    <Panel title="Hiring Tracker" subtitle="Recruitment quick count">
                        <div className="grid gap-2">
                            <StatusRow label="Offers generated" value={number(hiring.offersGenerated ?? hiring.offerReleased)} />
                            <StatusRow label="Signed offers" value={number(hiring.signedOffers)} tone="green" />
                            <StatusRow label="Pending signed offers" value={number(hiring.pendingSignedOffers ?? hiring.openPositions)} tone={hiring.pendingSignedOffers ? "amber" : "green"} />
                            <StatusRow label="Scheduled reporting" value={number(hiring.scheduledReporting ?? hiring.interviewScheduled)} />
                            <StatusRow label="Joined" value={number(hiring.joined)} tone="green" />
                        </div>
                    </Panel>

                    <Panel title="Complaint Health" subtitle="Grievance status">
                        <div className="grid gap-2">
                            <StatusRow label="Open complaints" value={number(complaints.open)} tone={complaints.open ? "red" : "green"} />
                            <StatusRow label="Resolved complaints" value={number(complaints.resolved)} tone="green" />
                            <StatusRow label="Needs attention" value={number(complaints.highPriority)} tone={complaints.highPriority ? "red" : "green"} />
                            <StatusRow label="Pending over 7 days" value={number(complaints.pendingBeyond7Days)} tone={complaints.pendingBeyond7Days ? "red" : "green"} />
                        </div>
                    </Panel>
                </section>

                <section className="mt-4 grid gap-4 xl:grid-cols-3">
                    <Panel title="Onboarding Tracker" subtitle="New joiner setup">
                        <div className="grid max-h-[270px] gap-2 overflow-y-auto pr-1">
                            {onboarding.length ? onboarding.map((item, index) => (
                                <div key={`${item.employee}-${index}`} className="rounded-xl border border-slate-200 p-3">
                                    <p className="font-black">{item.employee}</p>
                                    <p className="text-xs font-bold text-slate-500">{item.team}</p>
                                    <p className="mt-2 text-xs font-bold text-slate-700">
                                        Offer {item.offerLetterSent ? "done" : "pending"} | Docs {item.documentsCollected ? "done" : "pending"} | ID {item.idCreated ? "done" : "pending"}
                                    </p>
                                </div>
                            )) : <p className="rounded-xl bg-[var(--bg-secondary)] px-3 py-3 text-sm font-bold text-slate-500">No new joiners this month.</p>}
                        </div>
                    </Panel>

                    <Panel title="Offboarding Tracker" subtitle="Exit employee status">
                        <div className="grid max-h-[270px] gap-2 overflow-y-auto pr-1">
                            {offboarding.length ? offboarding.map((item, index) => (
                                <div key={`${item.employee}-${index}`} className="rounded-xl border border-slate-200 p-3">
                                    <p className="font-black">{item.employee}</p>
                                    <p className="text-xs font-bold text-slate-500">Last day: {dateText(item.lastWorkingDay)}</p>
                                    <p className="mt-2 text-xs font-bold text-slate-700">
                                        Handover {item.handoverStatus} | Asset {item.assetReturn} | Clearance {item.clearanceStatus}
                                    </p>
                                </div>
                            )) : <p className="rounded-xl bg-[var(--bg-secondary)] px-3 py-3 text-sm font-bold text-slate-500">No active offboarding items.</p>}
                        </div>
                    </Panel>

                    <Panel title="Documents Center" subtitle="Document completion count">
                        <div className="grid gap-2">
                            <StatusRow label="Offer letters" value={number(documents.offerLetters)} />
                            <StatusRow label="Experience letters" value={number(documents.experienceLetters)} />
                            <StatusRow label="Relieving letters" value={number(documents.relievingLetters)} />
                            <StatusRow label="ID proofs" value={number(documents.idProofs)} />
                            <StatusRow label="Salary documents" value={number(documents.salaryDocuments)} />
                            <StatusRow label="Policy acknowledgements" value={number(documents.policyAcknowledgements)} />
                        </div>
                    </Panel>
                </section>
            </main>
        </div>
    );
};

export default HrCommand;


